import React, { useState, useEffect } from 'react';

import Appearance from 'styles/Appearance.js';
import ProgressBar from 'views/ProgressBar.js';
import Utils from 'files/Utils.js';

const TextField = React.forwardRef(({
    animations, animateOnMount, append, appendContent, autoCapitalize, autoComplete, autoCompleteType, autoCorrect, className, containerStyle, expandWithText, fieldStyle, format, focused, icon, iconStyle, insetLabel, insetLabelStyle, insetContainerStyle, isSecure, isValid, keyboardType, loading, multiline, name, onChange, onBlur, onFocus, onKeyDown, onClick, onValidate, placeholder, prepend, spellCheck, textContentType, useDelay, value
}, ref) => {

    const [active, setActive] = useState(focused);
    const [textTimeout, setTextTimeout] = useState(false);
    const [text, setText] = useState(value !== null && value !== undefined ? value : '');

    const onFieldClick = () => {
        setActive(true);
        if(typeof(onClick) === 'function') {
            onClick();
        }
    }

    const onFieldBlur = () => {
        setActive(false);
        if(typeof(onBlur) === 'function') {
            onBlur();
        }
    }

    const onTextChange = (e) => {

        if(textTimeout) {
            clearTimeout(textTimeout);
        }

        let newText = e.target.value.replace(prepend || '', '').replace(append || '', '');
        if(format) {
            switch(format) {
                case 'phoneNumber':
                newText = Utils.formatPhoneNumber(newText);
                break;

                default:
                if(typeof(format) === 'function') {
                    newText = format(newText);
                }
            }
        }

        setText(newText);
        setTextTimeout(setTimeout(() => {
            if(typeof(onChange) === 'function') {
                onChange(newText);
            }
        }, useDelay === false ? 0 : 250));
    }

    const onKeyDownPress = (e) => {
        let keyCode = e.keyCode;
        let value = e.target.value;
        setTimeout(() => {
            if(typeof(onKeyDown) === 'function') {
                if(keyCode === 8) {
                    onKeyDown('backspace');
                    return;
                }
                onKeyDown(value);
            }
        }, useDelay === false ? 0 : 250);
    }

    const getField = () => {
        if(typeof(onClick) === 'function') {
            return (
                <span
                onClick={onFieldClick}
                className={'cursor-pointer'}
                style={{
                    flexGrow: 1,
                    borderWidth: 0,
                    fontSize: 12,
                    fontWeight: 500,
                    color: value ? Appearance.colors.text() : Appearance.colors.subText(),
                    backgroundColor: Appearance.colors.transparent,
                    ...fieldStyle
                }}>{value || placeholder}</span>
            )
        }
        if(isSecure || expandWithText !== true) {
            return (
                <input
                ref={ref}
                className={`dummy-field-input ${window.theme}`}
                onBlur={onFieldBlur}
                onClick={onFieldClick}
                onChange={onTextChange}
                onKeyDown={onKeyDownPress}
                value={(prepend || '') + (text || '') + (append || '')}
                placeholder={placeholder || ''}
                type={isSecure ? 'password' : 'text'}
                autoComplete={autoComplete || 'off'}
                autoCorrect={autoCorrect === false ? 'off':'on'}
                autoCapitalize={autoCapitalize === false ? 'off' : autoCapitalize}
                spellCheck={spellCheck === false ? 'false':'true'}
                style={{
                    border: 'none',
                    width: '100%',
                    height: 'auto',
                    fontSize: 12,
                    fontWeight: 500,
                    color: Appearance.colors.text(),
                    backgroundColor: Appearance.colors.transparent,
                    ...fieldStyle
                }} />
            )
        }

        return (
            <textarea
            ref={ref}
            className={`dummy-field-input ${window.theme}`}
            onBlur={onFieldBlur}
            onClick={onFieldClick}
            onChange={onTextChange}
            onKeyDown={onKeyDownPress}
            value={(prepend || '') + (text || '') + (append || '')}
            placeholder={placeholder || ''}
            type={isSecure === true ? 'password':'text'}
            autoComplete={autoComplete || 'off'}
            autoCorrect={autoCorrect === false ? 'off':'on'}
            autoCapitalize={autoCapitalize === false ? 'off' : autoCapitalize}
            spellCheck={spellCheck === false ? 'false':'true'}
            style={{
                border: 'none',
                width: '100%',
                height: 'auto',
                fontSize: 12,
                fontWeight: 500,
                resize: 'none',
                verticalAlign: 'middle',
                color: Appearance.colors.text(),
                paddingTop: expandWithText ? 0 : 18.5,
                backgroundColor: Appearance.colors.transparent,
                ...fieldStyle
            }} />
        )
    }

    useEffect(() => {
        setActive(focused);
    }, [focused])

    useEffect(() => {
        setText(value || '')
    }, [value]);

    return (
        <div
        className={`dummy-field ${window.theme} ${active ? 'active':''} ${className || ''}`}
        style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            flexGrow: 1,
            position: 'relative',
            height: expandWithText ? 'auto' : 35,
            minHeight: 35,
            ...containerStyle
        }}>
            {getIcon(icon)
                ?
                <div style={{
                    paddingRight: 8,
                    marginLeft: 4
                }}>
                    <i
                    className={getIcon(icon)}
                    style={{
                        color: Appearance.colors.softBorder(),
                        fontSize: 13
                    }}/>
                </div>
                :
                null
            }
            {insetLabel
                ?
                <div style={{
                    paddingRight: 8,
                    marginRight: 8,
                    borderRightWidth: 1,
                    borderRightColor: Appearance.colors.softBorder(),
                    ...insetContainerStyle
                }}>
                    <span style={{
                        fontSize: 10,
                        fontWeight: '600',
                        color: Appearance.colors.subText(),
                        ...insetLabelStyle
                    }}>{insetLabel}</span>
                </div>
                :
                null
            }
            {getField()}
            <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: 2
            }}>
                <div style={{
                    display: loading ? 'block' : 'none',
                    overflow: 'hidden',
                    borderRadius: 2,
                    height: 2
                }}>
                    <ProgressBar />
                </div>
            </div>
            {appendContent}
            {typeof(onValidate) === 'function' && onValidate(text)
                ?
                <img
                src={'images/checkmark-small-green.png'}
                style={{
                    width: 15,
                    height: 15,
                    objectFit: 'contain',
                    marginLeft: 8
                }} />
                :
                null
            }
        </div>
    )
})
export default TextField;

export const getIcon = (icon) => {
    switch(icon) {

        case 'user':
        return 'fas fa-user';

        case 'user-secret':
        return 'fas fa-user-secret';

        case 'email':
        return 'fas fa-envelope';

        case 'phone':
        return 'fas fa-phone';

        case 'calendar':
        return 'fas fa-calendar-alt';

        case 'house':
        return 'fas fa-home';

        case 'search':
        return 'fas fa-search';

        case 'lock':
        return 'fas fa-lock';

        case 'location':
        return 'fas fa-location-arrow';

        case 'payment':
        return 'fas fa-money-check-alt';

        case 'asterick':
        return 'fas fa-asterisk';

        case 'route':
        return 'fas fa-route';

        case 'guardian':
        return 'fas fa-user-friends';

        case 'title':
        return 'fas fa-signature';

        case 'description':
        return 'fas fa-stream';

        case 'featured':
        return 'fas fa-hand-holding-heart';

        case 'wifi':
        return 'fas fa-wifi';

        default:
            return;
    }
}
